import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatFileSize, getMimeType } from "@/shared/utils/fileHelpers";
import { getAttachmentViewingUrl } from "@/shared/services/uploadService";
import type {
  AnnouncementAttachment,
  AttachmentUploadItem,
} from "@/features/announcements/types/feed";

interface AttachmentListProps {
  attachments: AnnouncementAttachment[];
  uploads: AttachmentUploadItem[];
  onDelete: (attachmentId: number | string) => void;
  onRename: (attachmentId: number | string, newName: string) => void;
  onCancelUpload: (localId: string) => void;
}

/** Resolve MIME type from file_type column or fall back to display_name extension. */
const resolveType = (att: AnnouncementAttachment): string =>
  att.file_type || getMimeType(att.display_name);

const isImageAtt = (att: AnnouncementAttachment) =>
  resolveType(att).startsWith("image/");

/** Small thumbnail that lazily loads a presigned URL for images. */
const AttachmentThumbnail = ({
  attachment,
}: {
  attachment: AnnouncementAttachment;
}) => {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (isImageAtt(attachment)) {
      getAttachmentViewingUrl(attachment.attachment_id)
        .then(setUrl)
        .catch(() => {});
    }
  }, [attachment.attachment_id, attachment.file_type, attachment.display_name]);

  if (isImageAtt(attachment) && url) {
    return (
      <img
        src={url}
        alt={attachment.display_name}
        className="w-10 h-10 rounded object-cover border border-gray-200"
      />
    );
  }

  // PDF or generic file icon
  return (
    <div className="w-10 h-10 rounded bg-red-50 border border-red-200 flex items-center justify-center">
      <svg
        className="w-5 h-5 text-red-500"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
        />
      </svg>
    </div>
  );
};

/** Splits filename into basename + extension. */
const splitFileName = (name: string) => {
  const lastDot = name.lastIndexOf(".");
  if (lastDot <= 0) return { base: name, ext: "" };
  return { base: name.slice(0, lastDot), ext: name.slice(lastDot) };
};

export const AttachmentList = ({
  attachments,
  uploads,
  onDelete,
  onRename,
  onCancelUpload,
}: AttachmentListProps) => {
  const { t } = useTranslation();
  const [editingId, setEditingId] = useState<number | string | null>(null);
  const [editName, setEditName] = useState("");
  const [deletingId, setDeletingId] = useState<number | string | null>(null);
  const [openingId, setOpeningId] = useState<number | string | null>(null);

  const handleOpen = async (att: AnnouncementAttachment) => {
    setOpeningId(att.attachment_id);
    try {
      const url = await getAttachmentViewingUrl(att.attachment_id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      /* ignore — file may be unavailable */
    } finally {
      setOpeningId(null);
    }
  };

  const startRename = (att: AnnouncementAttachment) => {
    const { base } = splitFileName(att.display_name);
    setEditingId(att.attachment_id);
    setEditName(base);
  };

  const commitRename = (att: AnnouncementAttachment) => {
    const { ext } = splitFileName(att.display_name);
    const trimmed = editName.trim();
    if (trimmed && trimmed + ext !== att.display_name) {
      onRename(att.attachment_id, trimmed + ext);
    }
    setEditingId(null);
    setEditName("");
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditName("");
  };

  const handleDelete = async (id: number | string) => {
    setDeletingId(id);
    try {
      await onDelete(id);
    } finally {
      setDeletingId(null);
    }
  };

  if (attachments.length === 0 && uploads.length === 0) return null;

  return (
    <div className="space-y-2">
      {/* In-progress uploads */}
      {uploads.map((u) => (
        <div
          key={u.localId}
          className="flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200"
        >
          {/* Spinner or error icon */}
          {u.status === "error" ? (
            <div className="w-10 h-10 rounded bg-red-50 border border-red-200 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          ) : (
            <div className="w-10 h-10 rounded bg-indigo-50 border border-indigo-200 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-indigo-500 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            </div>
          )}

          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-700 truncate">{u.file.name}</p>
            {u.status === "uploading" && (
              <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                <div
                  className="bg-indigo-500 h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${u.progress}%` }}
                />
              </div>
            )}
            {u.status === "error" && (
              <p className="text-xs text-red-500 mt-0.5">
                {u.error || t("feed.form.upload_error")}
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={() => onCancelUpload(u.localId)}
            className="p-2 text-gray-400 hover:text-gray-600 active:text-gray-800 shrink-0"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      ))}

      {/* Saved attachments */}
      {attachments.map((att) => (
        <div
          key={att.attachment_id}
          className="flex items-center gap-3 px-3 py-2 bg-white rounded-lg border border-gray-200"
        >
          {/* Clickable thumbnail + name area */}
          <button
            type="button"
            onClick={() => editingId !== att.attachment_id && handleOpen(att)}
            disabled={openingId === att.attachment_id || editingId === att.attachment_id}
            className="flex items-center gap-3 flex-1 min-w-0 text-left hover:opacity-80 active:opacity-60 transition-opacity disabled:opacity-50"
            title={t("common.open")}
          >
            <AttachmentThumbnail attachment={att} />

            <div className="flex-1 min-w-0">
              {editingId === att.attachment_id ? (
                <div className="flex items-center gap-1">
                  <input
                    autoFocus
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitRename(att);
                      if (e.key === "Escape") cancelRename();
                    }}
                    onBlur={() => commitRename(att)}
                    onClick={(e) => e.stopPropagation()}
                    className="flex-1 px-1.5 py-0.5 text-sm border border-indigo-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-400">
                    {splitFileName(att.display_name).ext}
                  </span>
                </div>
              ) : (
                <p className="text-sm text-indigo-600 truncate underline-offset-2 hover:underline">
                  {att.display_name}
                </p>
              )}
              {att.file_size != null && (
                <p className="text-xs text-gray-400">
                  {formatFileSize(att.file_size)}
                </p>
              )}
            </div>
          </button>

          {/* Rename button */}
          {editingId !== att.attachment_id && (
            <button
              type="button"
              onClick={() => startRename(att)}
              className="p-2 text-gray-400 hover:text-indigo-600 active:text-indigo-800 shrink-0"
              title={t("common.rename")}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </button>
          )}

          {/* Delete button */}
          <button
            type="button"
            onClick={() => handleDelete(att.attachment_id)}
            disabled={deletingId === att.attachment_id}
            className="p-2 text-gray-400 hover:text-red-600 active:text-red-800 shrink-0 disabled:opacity-50"
            title={t("common.delete")}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
};
